import {
  AI_LABEL_ASPECT,
  AI_LABEL_INSET_FRACTION,
  AI_LABEL_SIZE_RANGE,
  AI_LABEL_SNAP_FRACTION,
  AI_LABEL_TEXT_FONT_FRACTION,
  AI_LABEL_WIDTH_FRACTION,
  AiLabelPosition,
  AiLabelType
} from './aiLabel.constants'

import { InterVariableFontFamily } from 'constants/styling/theme'

export interface ImageSize {
  width: number
  height: number
}

/** Resolved label box in image pixel coordinates. */
export interface AiLabelRect {
  left: number
  top: number
  width: number
  height: number
  /** Text labels only: font size in image px */
  fontSize?: number
}

/** Inputs the layout engine needs from the AI label state. */
export interface AiLabelLayoutInput {
  labelType: AiLabelType
  labelText: string
  position: AiLabelPosition | null
  sizeFraction: number | null
}

/** Snap guide lines to render while dragging, in image px. */
export interface AiLabelSnapGuides {
  vertical?: number
  horizontal?: number
}

export const getInset = (imageSize: ImageSize): number =>
  Math.min(imageSize.width, imageSize.height) * AI_LABEL_INSET_FRACTION

let measureContext: CanvasRenderingContext2D | null = null

/** Measures a text label's rendered width at a given font size, in image px. */
export function measureAiLabelText(text: string, fontSize: number): number {
  if (!measureContext) {
    measureContext = document.createElement('canvas').getContext('2d')
  }
  if (!measureContext) {
    // Canvas unavailable - approximate so the caller can still clamp
    return text.length * fontSize * 0.55
  }
  measureContext.font = `${fontSize}px ${InterVariableFontFamily}`
  return measureContext.measureText(text).width
}

const getDefaultSizeFraction = (labelType: AiLabelType): number =>
  labelType === AiLabelType.TEXT ? AI_LABEL_TEXT_FONT_FRACTION : AI_LABEL_WIDTH_FRACTION[labelType]

/**
 * Clamps a size fraction to the type's hard range, and for text additionally to
 * the largest font at which the line still fits the image width (never wraps).
 * Non-finite input (NaN/Infinity would sail through Math.min/max) resolves to
 * the type default so the range guarantee always holds.
 */
export function clampSizeFraction(input: Pick<AiLabelLayoutInput, 'labelType' | 'labelText'>, sizeFraction: number, imageSize: ImageSize): number {
  const range = AI_LABEL_SIZE_RANGE[input.labelType]
  const safe = Number.isFinite(sizeFraction) ? sizeFraction : getDefaultSizeFraction(input.labelType)
  let fraction = Math.min(range.max, Math.max(range.min, safe))

  if (input.labelType === AiLabelType.TEXT) {
    const maxWidth = imageSize.width - 2 * getInset(imageSize)
    while (fraction > range.min && measureAiLabelText(input.labelText, fraction * imageSize.width) > maxWidth) {
      fraction -= 0.001
    }
    // The 0.001-step descent can drift just under the floor - the range always wins
    fraction = Math.max(range.min, fraction)
  }
  return fraction
}

/** Box dimensions for a given (clamped) size fraction, in image px. */
export function getAiLabelBoxSize(input: AiLabelLayoutInput, sizeFraction: number, imageSize: ImageSize): { width: number, height: number, fontSize?: number } {
  if (input.labelType === AiLabelType.TEXT) {
    const fontSize = sizeFraction * imageSize.width
    return { width: measureAiLabelText(input.labelText, fontSize), height: fontSize, fontSize }
  }
  const width = sizeFraction * imageSize.width
  return { width, height: width / AI_LABEL_ASPECT[input.labelType] }
}

/** Keeps the whole box inside the image (resizing near an edge pushes it inward). */
export function clampRectToImage(rect: AiLabelRect, imageSize: ImageSize): AiLabelRect {
  const left = Math.min(Math.max(rect.left, 0), Math.max(0, imageSize.width - rect.width))
  const top = Math.min(Math.max(rect.top, 0), Math.max(0, imageSize.height - rect.height))
  return { ...rect, left, top }
}

/**
 * Resolves the label's box on the image, in image pixel coordinates.
 * Single source of truth for the live overlay (AiLabelOverlay) and the save-time
 * canvas draw (applyAiLabelToImage) so the export always matches the preview.
 *
 * position === null resolves to the default: bottom-right, inset 3% of the shorter side.
 */
export function getAiLabelRect(input: AiLabelLayoutInput, imageSize: ImageSize): AiLabelRect {
  const sizeFraction = clampSizeFraction(input, input.sizeFraction ?? getDefaultSizeFraction(input.labelType), imageSize)
  const box = getAiLabelBoxSize(input, sizeFraction, imageSize)

  if (input.position) {
    return clampRectToImage({
      left: input.position.x * imageSize.width - box.width / 2,
      top: input.position.y * imageSize.height - box.height / 2,
      ...box,
    }, imageSize)
  }

  const inset = getInset(imageSize)
  return clampRectToImage({
    left: imageSize.width - inset - box.width,
    top: imageSize.height - inset - box.height,
    ...box,
  }, imageSize)
}

/** The rect's centre as a normalised position (for committing a gesture to state). */
export const rectToPosition = (rect: AiLabelRect, imageSize: ImageSize): AiLabelPosition => ({
  x: (rect.left + rect.width / 2) / imageSize.width,
  y: (rect.top + rect.height / 2) / imageSize.height,
})

/**
 * Snaps a dragged rect to the edge insets and centre lines when within
 * AI_LABEL_SNAP_FRACTION of them, returning the snapped rect plus guide
 * line coordinates to render.
 */
export function snapRect(rect: AiLabelRect, imageSize: ImageSize): { rect: AiLabelRect, guides: AiLabelSnapGuides } {
  const inset = getInset(imageSize)
  const guides: AiLabelSnapGuides = {}
  let { left, top } = rect

  // A rect pinned against an image bound must stay there - reaching the edge beats
  // guide magnetism (the inset target used to yank edge placements back inward)
  const atXBound = rect.left <= 0.5 || rect.left >= imageSize.width - rect.width - 0.5
  const atYBound = rect.top <= 0.5 || rect.top >= imageSize.height - rect.height - 0.5

  const xTargets = [
    { edge: inset, apply: () => { left = inset } },
    { edge: imageSize.width - inset - rect.width, apply: () => { left = imageSize.width - inset - rect.width }, guide: imageSize.width - inset },
    { edge: (imageSize.width - rect.width) / 2, apply: () => { left = (imageSize.width - rect.width) / 2 }, guide: imageSize.width / 2 },
  ]
  const yTargets = [
    { edge: inset, apply: () => { top = inset } },
    { edge: imageSize.height - inset - rect.height, apply: () => { top = imageSize.height - inset - rect.height }, guide: imageSize.height - inset },
    { edge: (imageSize.height - rect.height) / 2, apply: () => { top = (imageSize.height - rect.height) / 2 }, guide: imageSize.height / 2 },
  ]

  const snapX = imageSize.width * AI_LABEL_SNAP_FRACTION
  const snapY = imageSize.height * AI_LABEL_SNAP_FRACTION

  for (const target of xTargets) {
    if (!atXBound && Math.abs(rect.left - target.edge) <= snapX) {
      target.apply()
      guides.vertical = target.guide ?? inset
      break
    }
  }
  for (const target of yTargets) {
    if (!atYBound && Math.abs(rect.top - target.edge) <= snapY) {
      target.apply()
      guides.horizontal = target.guide ?? inset
      break
    }
  }

  return { rect: { ...rect, left, top }, guides }
}
